/**
 * Catálogo de SFX de speak: cada sonido es una **figura musical** (specs de
 * notas/tiempos/timbre), no un archivo de audio. La síntesis vive en
 * `synth.ts`; el motor (cache, mute, gating, reproducción) en `engine.ts`.
 *
 * Estilo "cálido orgánico": voz `mallet` (marimba) al frente, `bell` (campanita
 * de vidrio) para el brillo, `pad` (swell) para los sonidos de sesión. La
 * DIFERENCIACIÓN entre sonidos sale de la figura (nº de notas, dirección,
 * intervalo, registro, largo), no del timbre — por eso son distinguibles.
 *
 * Ver `docs/speak-plan/43-sfx-diseno-sonoro.md` para el diseño completo.
 */
import { NOTE } from './notes.js';

/** Una nota de marimba: golpe con ataque rápido y decay exponencial. */
export interface SfxNote {
  /** Offset de inicio (s) desde el arranque del sonido. */
  t: number;
  /** Frecuencia (Hz) — usar siempre `NOTE.X`. */
  f: number;
  /** Duración del decay (s). */
  dur: number;
  /** Ganancia pico (0..1, pre-normalización). */
  g: number;
  /** Brillo del armónico de ataque (0..1, def 1). */
  bright?: number;
}
/** Una campanita de vidrio: partiales inarmónicos, decay largo. */
export interface SfxBell {
  t: number;
  f: number;
  dur: number;
  g: number;
}
/** Un pad de fondo: ataque lento, sostenido, cálido. */
export interface SfxPad {
  t: number;
  f: number;
  dur: number;
  g: number;
}
/**
 * Una ráfaga de ruido con un pasabanda que barre de `f0` a `f1` Hz: el golpe del
 * encaje, el whirr del anillo y el rumor/barrido del despegue de «Armá tu nave».
 */
export interface SfxNoise {
  t: number;
  dur: number;
  g: number;
  f0: number;
  f1: number;
}

export interface SoundSpec {
  /** Descripción dev-facing de la figura (por qué se distingue de las demás). */
  fig: string;
  /** Largo total del buffer (s). */
  len: number;
  /**
   * `true` = suena DURANTE el turno de voz (objetivos, juego). El motor lo
   * gatea para no disparar barge-in falso en modo realtime. Los sonidos
   * discretos de UI (no-`live`) suenan siempre (respetando el mute).
   */
  live?: boolean;
  notes?: readonly SfxNote[];
  bells?: readonly SfxBell[];
  pads?: readonly SfxPad[];
  noises?: readonly SfxNoise[];
}

export const SFX_SPECS = {
  // ── Feedback de aprendizaje ──────────────────────────────────────────────
  correct: { fig: '2 notas ↑ (quinta resuelta)', len: 0.6,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.3, g: 0.5 }, { t: 0.085, f: NOTE.G5, dur: 0.42, g: 0.55 }],
    bells: [{ t: 0.085, f: NOTE.G6, dur: 0.4, g: 0.07 }] },
  almost: { fig: '1 nota ↓ suave (sin resolver)', len: 0.6,
    notes: [{ t: 0, f: NOTE.E5, dur: 0.42, g: 0.44, bright: 0.4 }, { t: 0.14, f: NOTE.D5, dur: 0.42, g: 0.3, bright: 0.35 }] },
  'streak-up': { fig: '3 notas ↑ rápidas (momentum)', len: 0.5, live: true,
    notes: [{ t: 0, f: NOTE.E5, dur: 0.22, g: 0.42 }, { t: 0.06, f: NOTE.G5, dur: 0.22, g: 0.46 }, { t: 0.12, f: NOTE.A5, dur: 0.3, g: 0.5 }],
    bells: [{ t: 0.12, f: NOTE.A6, dur: 0.3, g: 0.05 }] },
  'match-tick': { fig: 'tap seco (madera)', len: 0.3,
    notes: [{ t: 0, f: NOTE.D5, dur: 0.12, g: 0.42, bright: 0.6 }] },
  // Mantener apretado «Me comprometo» (Tu rutina): una nota que sube con el anillo.
  'commit-hold-1': { fig: 'nota sola (mantener 1/3)', len: 0.45,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.3, g: 0.34, bright: 0.5 }] },
  'commit-hold-2': { fig: 'nota sola ↑ (mantener 2/3)', len: 0.45,
    notes: [{ t: 0, f: NOTE.E5, dur: 0.3, g: 0.38, bright: 0.55 }] },
  'commit-hold-3': { fig: 'nota sola ↑↑ (mantener 3/3)', len: 0.45,
    notes: [{ t: 0, f: NOTE.G5, dur: 0.3, g: 0.42, bright: 0.6 }] },

  // ── Celebraciones ────────────────────────────────────────────────────────
  'celebrate-small': { fig: 'arpegio de 3 ↑', len: 0.9,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.33, g: 0.46 }, { t: 0.075, f: NOTE.E5, dur: 0.33, g: 0.48 }, { t: 0.15, f: NOTE.G5, dur: 0.5, g: 0.5 }],
    bells: [{ t: 0.15, f: NOTE.C6, dur: 0.6, g: 0.1 }] },
  'celebrate-big': { fig: 'ráfaga de 5 ↑ + brillo', len: 1.25,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.28, g: 0.44 }, { t: 0.06, f: NOTE.E5, dur: 0.28, g: 0.46 }, { t: 0.12, f: NOTE.G5, dur: 0.28, g: 0.48 }, { t: 0.18, f: NOTE.A5, dur: 0.28, g: 0.5 }, { t: 0.26, f: NOTE.C6, dur: 0.6, g: 0.52 }],
    bells: [{ t: 0.26, f: NOTE.C6, dur: 0.7, g: 0.11 }, { t: 0.34, f: NOTE.E6, dur: 0.6, g: 0.07 }, { t: 0.42, f: NOTE.G6, dur: 0.5, g: 0.05 }] },
  'level-up': { fig: 'fanfarria de 4 + acorde', len: 1.4,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.3, g: 0.46 }, { t: 0.1, f: NOTE.E5, dur: 0.3, g: 0.48 }, { t: 0.2, f: NOTE.G5, dur: 0.3, g: 0.5 }, { t: 0.33, f: NOTE.C6, dur: 0.9, g: 0.55 }, { t: 0.33, f: NOTE.G5, dur: 0.9, g: 0.3 }, { t: 0.33, f: NOTE.E5, dur: 0.9, g: 0.24 }],
    bells: [{ t: 0.33, f: NOTE.C6, dur: 1.0, g: 0.11 }, { t: 0.55, f: NOTE.G6, dur: 0.85, g: 0.06 }] },
  'unit-mastered': { fig: 'acorde cálido resuelto', len: 1.2,
    notes: [{ t: 0, f: NOTE.C4, dur: 0.9, g: 0.3 }, { t: 0.02, f: NOTE.C5, dur: 0.85, g: 0.42 }, { t: 0.05, f: NOTE.E5, dur: 0.85, g: 0.4 }, { t: 0.08, f: NOTE.G5, dur: 0.9, g: 0.44 }],
    bells: [{ t: 0.4, f: NOTE.C6, dur: 0.7, g: 0.08 }] },
  graduation: { fig: 'fanfarria larga + acorde pleno', len: 1.85,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.28, g: 0.44 }, { t: 0.1, f: NOTE.E5, dur: 0.28, g: 0.46 }, { t: 0.2, f: NOTE.G5, dur: 0.28, g: 0.48 }, { t: 0.3, f: NOTE.C6, dur: 0.3, g: 0.5 }, { t: 0.4, f: NOTE.E6, dur: 0.3, g: 0.5 }, { t: 0.52, f: NOTE.C4, dur: 1.2, g: 0.32 }, { t: 0.52, f: NOTE.C5, dur: 1.1, g: 0.4 }, { t: 0.52, f: NOTE.G5, dur: 1.1, g: 0.34 }, { t: 0.52, f: NOTE.C6, dur: 1.2, g: 0.4 }],
    bells: [{ t: 0.52, f: NOTE.C6, dur: 1.3, g: 0.12 }, { t: 0.7, f: NOTE.G6, dur: 1.0, g: 0.07 }, { t: 0.9, f: NOTE.E6, dur: 0.9, g: 0.05 }] },
  // Festejo del objetivo BONUS (encargo 104): un escalón sobre `celebrate-big`.
  // Como el render se normaliza a pico 0.9, "más grande" no es más fuerte: es
  // más largo, más voces, registro más ancho y un acorde que queda sonando.
  'bonus-rain': { fig: 'ta-da-ta-DAA + cascada de campanitas', len: 2.1,
    notes: [{ t: 0, f: NOTE.G5, dur: 0.16, g: 0.46 }, { t: 0.08, f: NOTE.C6, dur: 0.18, g: 0.5 }, { t: 0.2, f: NOTE.G5, dur: 0.16, g: 0.46 }, { t: 0.28, f: NOTE.C6, dur: 0.18, g: 0.5 }, { t: 0.4, f: NOTE.E6, dur: 0.9, g: 0.54 }, { t: 0.4, f: NOTE.C5, dur: 0.9, g: 0.3 }, { t: 0.4, f: NOTE.G5, dur: 0.9, g: 0.32 }, { t: 0.4, f: NOTE.C4, dur: 1.0, g: 0.26 }],
    bells: [{ t: 0.4, f: NOTE.E6, dur: 0.9, g: 0.1 }, { t: 0.56, f: NOTE.A6, dur: 0.5, g: 0.06 }, { t: 0.66, f: NOTE.G6, dur: 0.5, g: 0.06 }, { t: 0.76, f: NOTE.E6, dur: 0.5, g: 0.06 }, { t: 0.86, f: NOTE.A6, dur: 0.5, g: 0.05 }, { t: 0.98, f: NOTE.C6, dur: 0.5, g: 0.06 }, { t: 1.08, f: NOTE.G6, dur: 0.5, g: 0.05 }, { t: 1.2, f: NOTE.E6, dur: 0.5, g: 0.05 }, { t: 1.34, f: NOTE.A6, dur: 0.6, g: 0.04 }] },

  // ── Objetivos en vivo ────────────────────────────────────────────────────
  'objective-met': { fig: '2 notas ↑ sutiles', len: 0.5, live: true,
    notes: [{ t: 0, f: NOTE.E5, dur: 0.28, g: 0.34, bright: 0.5 }, { t: 0.09, f: NOTE.G5, dur: 0.36, g: 0.38, bright: 0.5 }] },

  // ── Juego / scoreboard ───────────────────────────────────────────────────
  'game-correct': { fig: '2 notas ↑ punchy (alto)', len: 0.45, live: true,
    notes: [{ t: 0, f: NOTE.G5, dur: 0.2, g: 0.5 }, { t: 0.06, f: NOTE.C6, dur: 0.3, g: 0.55 }],
    bells: [{ t: 0.06, f: NOTE.C6, dur: 0.35, g: 0.09 }] },
  'game-miss': { fig: '3 notas ↓ suaves (aww)', len: 0.5, live: true,
    notes: [{ t: 0, f: NOTE.G5, dur: 0.2, g: 0.4, bright: 0.5 }, { t: 0.08, f: NOTE.E5, dur: 0.2, g: 0.36, bright: 0.45 }, { t: 0.16, f: NOTE.C5, dur: 0.32, g: 0.34, bright: 0.4 }] },
  'game-streak': { fig: '4 notas ↑ veloces (climbing)', len: 0.6, live: true,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.16, g: 0.44 }, { t: 0.05, f: NOTE.E5, dur: 0.16, g: 0.46 }, { t: 0.1, f: NOTE.G5, dur: 0.16, g: 0.48 }, { t: 0.15, f: NOTE.C6, dur: 0.32, g: 0.52 }],
    bells: [{ t: 0.15, f: NOTE.C6, dur: 0.4, g: 0.08 }] },
  'round-win': { fig: 'flourish + acorde (playful)', len: 1.0, live: true,
    notes: [{ t: 0, f: NOTE.G5, dur: 0.24, g: 0.48 }, { t: 0.09, f: NOTE.C6, dur: 0.6, g: 0.54 }, { t: 0.09, f: NOTE.E5, dur: 0.6, g: 0.3 }, { t: 0.09, f: NOTE.G5, dur: 0.6, g: 0.34 }],
    bells: [{ t: 0.09, f: NOTE.C6, dur: 0.7, g: 0.1 }, { t: 0.25, f: NOTE.G6, dur: 0.55, g: 0.06 }] },
  'secret-word-deal': { fig: 'shimmer ↓ (anticipación)', len: 0.7,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.5, g: 0.3, bright: 0.4 }],
    bells: [{ t: 0, f: NOTE.C6, dur: 0.5, g: 0.08 }, { t: 0.08, f: NOTE.A5, dur: 0.45, g: 0.07 }, { t: 0.16, f: NOTE.G5, dur: 0.4, g: 0.06 }] },
  'secret-word-reveal': { fig: 'sparkle ↑ (ta-da)', len: 0.6, live: true,
    notes: [{ t: 0, f: NOTE.A5, dur: 0.22, g: 0.44 }, { t: 0.07, f: NOTE.C6, dur: 0.4, g: 0.5 }],
    bells: [{ t: 0.07, f: NOTE.C6, dur: 0.5, g: 0.11 }, { t: 0.07, f: NOTE.E6, dur: 0.45, g: 0.06 }] },

  // ── Ciclo de sesión ──────────────────────────────────────────────────────
  'session-start': { fig: 'swell cálido ↑ (conecta)', len: 1.1,
    pads: [{ t: 0, f: NOTE.C4, dur: 1.0, g: 0.12 }, { t: 0, f: NOTE.G4, dur: 1.0, g: 0.1 }],
    notes: [{ t: 0.12, f: NOTE.C5, dur: 0.4, g: 0.4 }, { t: 0.28, f: NOTE.G5, dur: 0.55, g: 0.44 }],
    bells: [{ t: 0.28, f: NOTE.G6, dur: 0.5, g: 0.05 }] },
  'session-end': { fig: '3 notas ↓ + pad (cierre)', len: 1.1,
    pads: [{ t: 0, f: NOTE.C4, dur: 1.0, g: 0.1 }],
    notes: [{ t: 0, f: NOTE.G5, dur: 0.3, g: 0.42, bright: 0.5 }, { t: 0.14, f: NOTE.E5, dur: 0.3, g: 0.38, bright: 0.45 }, { t: 0.28, f: NOTE.C5, dur: 0.6, g: 0.4, bright: 0.4 }] },
  'connection-lost': { fig: '2 notas ↓ (caída suave)', len: 0.6,
    notes: [{ t: 0, f: NOTE.D5, dur: 0.28, g: 0.38, bright: 0.4 }, { t: 0.13, f: NOTE.A4, dur: 0.4, g: 0.34, bright: 0.35 }] },
  'idle-warning': { fig: 'campanita sola (nudge)', len: 0.5,
    bells: [{ t: 0, f: NOTE.G5, dur: 0.5, g: 0.18 }] },
  resume: { fig: '3 notas ↑ + pad (welcome back)', len: 0.8,
    pads: [{ t: 0, f: NOTE.G3, dur: 0.7, g: 0.08 }],
    notes: [{ t: 0, f: NOTE.G4, dur: 0.28, g: 0.4 }, { t: 0.1, f: NOTE.C5, dur: 0.28, g: 0.44 }, { t: 0.2, f: NOTE.E5, dur: 0.4, g: 0.46 }],
    bells: [{ t: 0.2, f: NOTE.C6, dur: 0.4, g: 0.06 }] },

  // ── Completados / captura ────────────────────────────────────────────────
  'card-flip': { fig: 'doble tick seco', len: 0.3,
    notes: [{ t: 0, f: NOTE.D5, dur: 0.1, g: 0.4, bright: 0.6 }, { t: 0.045, f: NOTE.E5, dur: 0.14, g: 0.4, bright: 0.6 }] },
  'drill-complete': { fig: '↑ + acorde de cierre', len: 0.9,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.26, g: 0.44 }, { t: 0.09, f: NOTE.G5, dur: 0.3, g: 0.48 }, { t: 0.2, f: NOTE.C6, dur: 0.5, g: 0.5 }, { t: 0.2, f: NOTE.E5, dur: 0.5, g: 0.28 }],
    bells: [{ t: 0.2, f: NOTE.C6, dur: 0.55, g: 0.08 }] },
  'word-saved': { fig: 'blip + thunk grave (captura)', len: 0.5,
    notes: [{ t: 0, f: NOTE.G5, dur: 0.2, g: 0.46, bright: 0.6 }, { t: 0.05, f: NOTE.C4, dur: 0.3, g: 0.34 }],
    bells: [{ t: 0, f: NOTE.C6, dur: 0.3, g: 0.07 }] },
  'quiz-start': { fig: '3 notas ↑ bajas (here we go)', len: 0.6,
    notes: [{ t: 0, f: NOTE.G4, dur: 0.2, g: 0.42 }, { t: 0.08, f: NOTE.C5, dur: 0.2, g: 0.46 }, { t: 0.16, f: NOTE.E5, dur: 0.34, g: 0.5 }],
    bells: [{ t: 0.16, f: NOTE.G5, dur: 0.35, g: 0.06 }] },

  // ── «Armá tu nave» (encargo 117): el recorrido de las 7 piezas ────────────
  // Toque de un módulo que falta: tap de madera con un brillo arriba.
  'ship-tap': { fig: 'tap seco + brillo', len: 0.35,
    notes: [{ t: 0, f: NOTE.D5, dur: 0.12, g: 0.42, bright: 0.6 }],
    bells: [{ t: 0.02, f: NOTE.A6, dur: 0.18, g: 0.04 }] },
  // Toque de un módulo que ya está: dos notas cortas que suben.
  'ship-piece': { fig: '2 notas ↑ cortas', len: 0.4,
    notes: [{ t: 0, f: NOTE.G5, dur: 0.16, g: 0.4, bright: 0.6 }, { t: 0.05, f: NOTE.A5, dur: 0.22, g: 0.42 }] },
  // Pieza ganada: arpegio de 4 que sube y queda abierto (pide el encaje).
  'ship-win': { fig: 'arpegio de 4 ↑ abierto', len: 1.0,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.3, g: 0.44 }, { t: 0.07, f: NOTE.E5, dur: 0.3, g: 0.46 }, { t: 0.14, f: NOTE.G5, dur: 0.3, g: 0.48 }, { t: 0.21, f: NOTE.A5, dur: 0.55, g: 0.5 }],
    bells: [{ t: 0.21, f: NOTE.A6, dur: 0.6, g: 0.08 }, { t: 0.3, f: NOTE.E6, dur: 0.5, g: 0.05 }] },
  // El módulo se acopla: golpe grave (madera + ruido corto) y la quinta que resuelve.
  'ship-dock': { fig: 'golpe grave + quinta resuelta', len: 0.7,
    notes: [{ t: 0, f: NOTE.C4, dur: 0.22, g: 0.5, bright: 0.3 }, { t: 0.04, f: NOTE.G5, dur: 0.3, g: 0.36 }, { t: 0.1, f: NOTE.C6, dur: 0.45, g: 0.4 }],
    bells: [{ t: 0.1, f: NOTE.C6, dur: 0.45, g: 0.07 }],
    noises: [{ t: 0, dur: 0.09, g: 0.35, f0: 1800, f1: 500 }] },
  // Girar el anillo con el dedo (o empujar la nave): whirr que sube.
  'ship-spin': { fig: 'whirr ↑', len: 0.6,
    noises: [{ t: 0, dur: 0.5, g: 0.12, f0: 300, f1: 2200 }],
    bells: [{ t: 0.38, f: NOTE.E6, dur: 0.25, g: 0.04 }] },
  // Nave completa: la fanfarria de level-up.
  'ship-complete': { fig: 'fanfarria de 4 + acorde', len: 1.4,
    notes: [{ t: 0, f: NOTE.C5, dur: 0.3, g: 0.46 }, { t: 0.1, f: NOTE.E5, dur: 0.3, g: 0.48 }, { t: 0.2, f: NOTE.G5, dur: 0.3, g: 0.5 }, { t: 0.33, f: NOTE.C6, dur: 0.9, g: 0.55 }, { t: 0.33, f: NOTE.G5, dur: 0.9, g: 0.3 }, { t: 0.33, f: NOTE.E5, dur: 0.9, g: 0.24 }],
    bells: [{ t: 0.33, f: NOTE.C6, dur: 1.0, g: 0.11 }, { t: 0.55, f: NOTE.G6, dur: 0.85, g: 0.06 }] },
  // Cuenta regresiva: una campanita por número.
  'ship-count': { fig: 'campanita sola', len: 0.4,
    bells: [{ t: 0, f: NOTE.G5, dur: 0.35, g: 0.2 }] },
  // Los módulos se funden con la nave: la campanita una octava arriba.
  'ship-ignite': { fig: 'campanita aguda', len: 0.5,
    bells: [{ t: 0, f: NOTE.C6, dur: 0.45, g: 0.22 }] },
  // Despegue: rumor grave que crece y un barrido que sube; al salir, la fanfarria de graduación.
  'ship-launch': { fig: 'rumor ↑ + barrido + fanfarria', len: 3.4,
    pads: [{ t: 0, f: NOTE.C3, dur: 1.6, g: 0.16 }, { t: 0.2, f: NOTE.G3, dur: 1.5, g: 0.12 }],
    noises: [{ t: 0, dur: 1.7, g: 0.32, f0: 120, f1: 900 }, { t: 1.2, dur: 1.0, g: 0.22, f0: 900, f1: 6000 }],
    notes: [{ t: 1.7, f: NOTE.C5, dur: 0.28, g: 0.44 }, { t: 1.8, f: NOTE.E5, dur: 0.28, g: 0.46 }, { t: 1.9, f: NOTE.G5, dur: 0.28, g: 0.48 }, { t: 2.0, f: NOTE.C6, dur: 0.3, g: 0.5 }, { t: 2.1, f: NOTE.E6, dur: 0.3, g: 0.5 }, { t: 2.22, f: NOTE.C4, dur: 1.1, g: 0.32 }, { t: 2.22, f: NOTE.C5, dur: 1.0, g: 0.4 }, { t: 2.22, f: NOTE.G5, dur: 1.0, g: 0.34 }, { t: 2.22, f: NOTE.C6, dur: 1.1, g: 0.4 }],
    bells: [{ t: 2.22, f: NOTE.C6, dur: 1.1, g: 0.12 }, { t: 2.4, f: NOTE.G6, dur: 0.9, g: 0.07 }, { t: 2.6, f: NOTE.E6, dur: 0.8, g: 0.05 }] },
} satisfies Record<string, SoundSpec>;

/** Nombre válido de SFX (clave de `SFX_SPECS`). */
export type SfxName = keyof typeof SFX_SPECS;

/**
 * Valida una spec: toda frecuencia/tiempo/duración/ganancia debe ser un número
 * finito y coherente. Fail-fast: una nota mal definida (typo, `undefined`) tira
 * error acá en vez de romper silenciosamente en runtime. Ver `notes.ts`.
 */
export function validateSpec(name: string, spec: SoundSpec): void {
  if (!Number.isFinite(spec.len) || spec.len <= 0) {
    throw new Error(`SFX '${name}': len inválido (${spec.len})`);
  }
  const voices: ReadonlyArray<readonly (SfxNote | SfxBell | SfxPad)[] | undefined> = [
    spec.notes,
    spec.bells,
    spec.pads,
  ];
  const kinds = ['notes', 'bells', 'pads'] as const;
  (spec.noises ?? []).forEach((r, i) => {
    const where = `SFX '${name}' noises[${i}]`;
    if (!Number.isFinite(r.f0) || r.f0 <= 0) throw new Error(`${where}: f0 inválido (${r.f0})`);
    if (!Number.isFinite(r.f1) || r.f1 <= 0) throw new Error(`${where}: f1 inválido (${r.f1})`);
    if (!Number.isFinite(r.t) || r.t < 0) throw new Error(`${where}: t inválido (${r.t})`);
    if (!Number.isFinite(r.dur) || r.dur <= 0) throw new Error(`${where}: dur inválido (${r.dur})`);
    if (!Number.isFinite(r.g) || r.g < 0) throw new Error(`${where}: g inválido (${r.g})`);
  });
  voices.forEach((arr, vi) => {
    (arr ?? []).forEach((v, i) => {
      const where = `SFX '${name}' ${kinds[vi]}[${i}]`;
      if (!Number.isFinite(v.f)) throw new Error(`${where}: frecuencia no-finita (${v.f})`);
      if (!Number.isFinite(v.t) || v.t < 0) throw new Error(`${where}: t inválido (${v.t})`);
      if (!Number.isFinite(v.dur) || v.dur <= 0) throw new Error(`${where}: dur inválido (${v.dur})`);
      if (!Number.isFinite(v.g) || v.g < 0) throw new Error(`${where}: g inválido (${v.g})`);
    });
  });
}

/** Corre `validateSpec` sobre todo el catálogo. Lanzado al importar el módulo. */
export function validateAllSpecs(): void {
  for (const [name, spec] of Object.entries(SFX_SPECS)) {
    validateSpec(name, spec);
  }
}

// Fail-fast en tiempo de carga: si una spec quedó mal, el módulo no importa.
validateAllSpecs();
